// src/components/roadmap/RoadmapPhaseCard.tsx
import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { CheckCircle, Clock, Sparkles } from 'lucide-react';

// Utility to get RGB values for our dynamic shadow color
const colorToRgb: Record<string, string> = {
  'emerald-500': '16, 185, 129',
  'sky-500': '14, 165, 233',
  'purple-500': '168, 85, 247',
};

// Sub-component for a single item (no changes needed here)
const RoadmapItem: React.FC<{ text: string; status: 'launched' | 'in-progress' | 'future' }> = ({ text, status }) => {
  const Icon = status === 'launched' ? CheckCircle : status === 'in-progress' ? Clock : Sparkles;
  const iconColor = status === 'launched' ? 'text-emerald-500' : status === 'in-progress' ? 'text-sky-500' : 'text-purple-500';

  return (
    <li className="flex items-start gap-3">
      <Icon size={18} className={clsx(iconColor, "mt-0.5 shrink-0")} />
      <span className="text-slate-700">{text}</span>
    </li>
  );
};

interface PhaseData {
  title: string;
  status: string;
  items: Record<string, string>;
}

interface RoadmapPhaseCardProps {
  index: number;
  phaseData: PhaseData;
  statusColor: string;
  borderColor: string;
}

export const RoadmapPhaseCard: React.FC<RoadmapPhaseCardProps> = ({ index, phaseData, statusColor, borderColor }) => {
  const isRightSide = index % 2 !== 0;

  const containerVariants = {
    hidden: { opacity: 0, x: isRightSide ? 50 : -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const getStatusForItems = (phaseIndex: number): 'launched' | 'in-progress' | 'future' => {
    if (phaseIndex === 0) return 'launched';
    if (phaseIndex === 1) return 'in-progress';
    return 'future';
  };
  
  // Extract color name (e.g., "emerald-500") from the borderColor class (e.g., "border-emerald-500")
  const shadowColorName = borderColor.split('-').slice(1).join('-');
  const shadowRgb = colorToRgb[shadowColorName] || '17, 24, 39'; // Fallback to black

  return (
    <motion.div
      className={clsx("mb-8 flex justify-between items-center w-full", {
        "flex-row-reverse": isRightSide,
      })}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <div className="order-1 w-5/12"></div>
      <div className="z-20 flex items-center order-1 bg-slate-800 shadow-xl w-8 h-8 rounded-full">
        <h1 className="mx-auto font-semibold text-lg text-white">{index + 1}</h1>
      </div>

      {/* The Card - Now uses CSS for hover animation, not Framer Motion */}
      <div
        className={clsx(
            "order-1 rounded-xl border-2 w-5/12 px-6 py-5 cursor-pointer",
            "bg-white",
            borderColor,
            // --- ANIMATION & SHADOW CLASSES ---
            "transform transition-all duration-150 ease-out",
            "hover:-translate-y-1.5",
            isRightSide ? "hover:translate-x-1.5" : "hover:-translate-x-1.5",
            // Use arbitrary values with CSS variables for dynamic colored shadow
            `hover:shadow-[6px_6px_0_0_rgba(var(--shadow-color),0.9)]`,
            "active:translate-y-0 active:translate-x-0 active:shadow-none"
        )}
        // Set the CSS variable for the shadow color
        style={{ '--shadow-color': shadowRgb } as React.CSSProperties}
      >
        <h3 className={clsx("mb-1 font-bold text-gray-900 text-lg")}>{phaseData.title}</h3>
        <p className={clsx("text-sm font-semibold leading-snug tracking-wide mb-4", statusColor)}>
          {phaseData.status.toUpperCase()}
        </p>
        <ul className="space-y-3">
          {Object.values(phaseData.items).map((itemText, itemIndex) => (
            <RoadmapItem key={itemIndex} text={itemText} status={getStatusForItems(index)} />
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

